import { pageOf } from '@contracts/schemas/common.schema';
import { AccessMatrixSchema } from '@contracts/schemas/permissions.schema';
import { UserAccessSchema } from '@contracts/schemas/access.schema';
import { LabGeofenceSchema } from '@contracts/schemas/user.schema';
import { LabSettingsSchema } from '@contracts/schemas/settings.schema';
import { CatalogItemSchema } from '@contracts/schemas/catalog.schema';
import {
  AvatarResultSchema,
  ProfileSchema,
  UserCaseRowSchema,
  UserSchema,
} from '@contracts/schemas/user.schema';
import { ProblemDetailsSchema } from '@contracts/schemas/common.schema';
import { PwaSettingsSchema } from '@contracts/schemas/pwa.schema';
import { env } from '@/shared/config/env';
import { getAccessToken } from '../tokenStore';
import type {
  AdminRepository,
  AppIconUpload,
  PwaSettings,
  CatalogItemEdit,
  NewCatalogItem,
  NewUser,
  UserCasePage,
  UserEdit,
  UserPage,
} from '@/application/ports/AdminRepository';
import type { CatalogItemView } from '@/application/ports/JobOrderRepository';
import type {
  AccessMatrix,
  LabGeofence,
  LabSettings,
  User,
  UserAccess,
  UserAccessOverride,
  ProfileRange,
  UserProfile,
} from '@/domain';
import { ApiError, request, send } from '../client';

const UserPageSchema = pageOf(UserSchema);
const UserCasePageSchema = pageOf(UserCaseRowSchema);

export class HttpAdminRepository implements AdminRepository {
  async users(): Promise<UserPage> {
    // Every user, including revoked ones: the screen has to offer restore.
    return request('/users?pageSize=200', { schema: UserPageSchema });
  }

  async userCases(userId: string, page: number, pageSize: number): Promise<UserCasePage> {
    return request(`/users/${userId}/cases?page=${String(page)}&pageSize=${String(pageSize)}`, {
      schema: UserCasePageSchema,
    });
  }

  async createUser(user: NewUser): Promise<User> {
    return request('/users', { method: 'POST', body: user, schema: UserSchema });
  }

  async pwaSettings(): Promise<PwaSettings> {
    return request('/pwa-settings', { schema: PwaSettingsSchema });
  }

  async savePwaSettings(colors: { themeColor: string; backgroundColor: string }): Promise<PwaSettings> {
    return request('/pwa-settings', { method: 'PUT', body: colors, schema: PwaSettingsSchema });
  }

  /** Multipart, four PNG parts. A refusal carries the server's words. */
  async uploadAppIcons(icons: AppIconUpload): Promise<PwaSettings> {
    const body = new FormData();
    body.append('icon192', icons.icon192, 'icon-192.png');
    body.append('icon512', icons.icon512, 'icon-512.png');
    body.append('maskable512', icons.maskable512, 'icon-maskable-512.png');
    body.append('appleTouch180', icons.appleTouch180, 'apple-touch-icon.png');
    const token = getAccessToken();
    const response = await send(`${env.apiBaseUrl}/pwa-settings/icons`, {
      method: 'POST',
      body,
      credentials: 'include',
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const problem = ProblemDetailsSchema.safeParse(await response.json().catch(() => null));
      throw new ApiError(response.status, problem.success ? problem.data : null, 'The icon could not be saved');
    }
    return PwaSettingsSchema.parse(await response.json());
  }

  async resetAppIcons(): Promise<PwaSettings> {
    return request('/pwa-settings/icons', { method: 'DELETE', schema: PwaSettingsSchema });
  }

  async profile(userId: string, range: ProfileRange): Promise<UserProfile> {
    return request(`/users/${userId}/profile?range=${range}`, { schema: ProfileSchema });
  }

  /** Multipart, as a case photograph is. A refusal carries the server's words. */
  async setAvatar(userId: string, file: File): Promise<string> {
    const body = new FormData();
    body.append('file', file);
    const token = getAccessToken();
    const response = await send(`${env.apiBaseUrl}/users/${userId}/avatar`, {
      method: 'POST',
      body,
      credentials: 'include',
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const problem = ProblemDetailsSchema.safeParse(await response.json().catch(() => null));
      throw new ApiError(response.status, problem.success ? problem.data : null, 'The photo could not be saved');
    }
    return AvatarResultSchema.parse(await response.json()).avatarUrl;
  }

  async removeAvatar(userId: string): Promise<void> {
    await request(`/users/${userId}/avatar`, { method: 'DELETE' });
  }

  async updateUser(id: string, edit: UserEdit): Promise<User> {
    return request(`/users/${id}`, { method: 'PATCH', body: edit, schema: UserSchema });
  }

  async revokeUser(id: string): Promise<User> {
    return request(`/users/${id}/revoke`, { method: 'POST', schema: UserSchema });
  }

  async restoreUser(id: string): Promise<User> {
    return request(`/users/${id}/restore`, { method: 'POST', schema: UserSchema });
  }

  async createCatalogItem(item: NewCatalogItem): Promise<CatalogItemView> {
    return request('/catalog/items', { method: 'POST', body: item, schema: CatalogItemSchema });
  }

  async updateCatalogItem(
    id: string,
    rowVersion: string,
    edit: CatalogItemEdit,
  ): Promise<CatalogItemView> {
    return request(`/catalog/items/${id}`, {
      method: 'PATCH',
      body: edit,
      rowVersion,
      schema: CatalogItemSchema,
    });
  }

  async setUserPassword(id: string, newPassword: string): Promise<void> {
    await request(`/users/${id}/password`, { method: 'POST', body: { newPassword } });
  }

  async userAccess(userId: string): Promise<UserAccess> {
    return request(`/users/${userId}/access`, { schema: UserAccessSchema });
  }

  async saveUserAccess(
    userId: string,
    rowVersion: string,
    access: UserAccessOverride & { note: string | null },
  ): Promise<UserAccess> {
    return request(`/users/${userId}/access`, {
      method: 'PUT',
      body: {
        grantedPermissions: access.grantedPermissions,
        grantedViews: access.grantedViews,
        deniedPermissions: access.deniedPermissions,
        deniedViews: access.deniedViews,
        note: access.note,
      },
      // Two managers editing one person at once get a 409 they can read.
      rowVersion,
      schema: UserAccessSchema,
    });
  }

  async accessMatrix(): Promise<AccessMatrix> {
    const matrix = await request('/access-matrix', { schema: AccessMatrixSchema });
    return matrix as unknown as AccessMatrix;
  }

  async saveAccessMatrix(matrix: AccessMatrix): Promise<AccessMatrix> {
    const saved = await request('/access-matrix', {
      method: 'PUT',
      body: matrix,
      // Section 7.9. Two managers editing the matrix at once is realistic, and
      // without this the second save silently discards the first.
      rowVersion: matrix.rowVersion,
      schema: AccessMatrixSchema,
    });
    return saved as unknown as AccessMatrix;
  }

  async geofence(): Promise<LabGeofence> {
    return request('/geofence', { schema: LabGeofenceSchema });
  }

  async saveGeofence(fence: LabGeofence): Promise<LabGeofence> {
    return request('/geofence', { method: 'PUT', body: fence, schema: LabGeofenceSchema });
  }

  async settings(): Promise<LabSettings> {
    return request('/settings', { schema: LabSettingsSchema });
  }

  async saveSettings(settings: LabSettings): Promise<LabSettings> {
    return request('/settings', {
      method: 'PUT',
      body: {
        routing: settings.routing,
        workProof: settings.workProof,
        // When the day ends, or null for never. Left out when the API this build
        // was read from did not carry it, so nothing is switched off by accident.
        ...(settings.dayEndsAt === undefined ? {} : { dayEndsAt: settings.dayEndsAt }),
        ...(settings.afterHoursEndsAt === undefined
          ? {}
          : { afterHoursEndsAt: settings.afterHoursEndsAt }),
        // Whether Final Check may be taken over. Left out when the screen did not
        // say, so nothing is switched by accident.
        ...(settings.takeOverAtFinalCheck === undefined
          ? {}
          : { takeOverAtFinalCheck: settings.takeOverAtFinalCheck }),
      },
      // Two managers changing the floor's behaviour at once get a 409, not a
      // silent last-writer-wins. Section 7.9.
      rowVersion: settings.rowVersion,
      schema: LabSettingsSchema,
    });
  }
}
